'use client';

import React, { useEffect, useMemo, useState } from 'react';

type Props = {
  gifId: string;
  previewUrl?: string;
  children?: React.ReactNode;
};

type TenorMediaFormat = { url: string; dims?: [number, number] };
type TenorPost = {
  id: string;
  media_formats: Record<string, TenorMediaFormat>;
};
type TenorPostsResponse = { results: TenorPost[] };

const API_KEY = process.env.NEXT_PUBLIC_TENOR_GIF_API_KEY!;
const TENOR_BASE = 'https://tenor.googleapis.com/v2';
const CLIENT_KEY = 'player3_web';

const DISPLAY_FORMATS = ['mediumgif', 'gif', 'tinygif'] as const;

export const GifBlock = ({ gifId, previewUrl, children }: Props) => {
  const [url, setUrl] = useState<string | null>(null);

  const effectiveUrl = useMemo(() => previewUrl || url, [previewUrl, url]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!gifId) return;

      try {
        const endpoint =
          `${TENOR_BASE}/posts?ids=${encodeURIComponent(gifId)}` +
          `&key=${encodeURIComponent(API_KEY)}` +
          `&client_key=${encodeURIComponent(CLIENT_KEY)}`;

        const res = await fetch(endpoint);
        if (!res.ok) throw new Error('Failed to load Tenor GIF');

        const json = (await res.json()) as TenorPostsResponse;
        const first = json?.results?.[0];
        const mf = first?.media_formats ?? {};

        const media = DISPLAY_FORMATS.map((k) => mf[k]).find((x) => x?.url);
        const nextUrl = media?.url ?? null;

        if (!cancelled) setUrl(nextUrl);
      } catch (e) {
        console.error(e);
        if (!cancelled) setUrl(null);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [gifId]);

  if (!gifId) return null;

  return (
    <div className="flex flex-col gap-1 mb-2 md:mb-0">
      <div className="relative mt-2 w-fit group">
        {effectiveUrl ? (
          <img
            src={effectiveUrl}
            alt="GIF"
            className="md:h-[155px] h-[100px] rounded-lg relative object-cover"
            loading="lazy"
          />
        ) : (
          <div className="md:h-[155px] h-[100px] md:w-[155px] w-[100px] rounded-lg bg-black/5" />
        )}
        {children}
      </div>
    </div>
  );
};

